// Server-derived to-dos for the viewer (GET /me/actions). Phase 4 adds kind
// 'practice' (a practical exercise to do or resubmit): with a session it opens
// the session sheet; without one (course-only assignment) the parent opens the
// assignment's practice dialog via onPractice.
import { useT } from '../lib/i18n';
import React from 'react';
import { AlertTriangle, BookOpenCheck, CheckSquare, ClipboardList, Video } from 'lucide-react';
import { Card, CardContent, CardTitle } from '@/features/shared/components/ui/card';
import type { LearningAction } from '@/services/learningService';
import { formatSessionRange, sessionZoneNote } from '../lib/format';

const ICON: Record<LearningAction['kind'], React.ComponentType<{ className?: string }>> = {
  prepare: BookOpenCheck, join: Video, complete: CheckSquare, overdue: AlertTriangle, practice: ClipboardList,
};

interface Props {
  actions: LearningAction[];
  onOpen: (sessionId: string) => void;
  /** Phase 4: a 'practice' action — receives the whole action (assignment_id + optional session_id). */
  onPractice?: (action: LearningAction) => void;
}

export const ActionRequired: React.FC<Props> = ({ actions, onOpen, onPractice }) => {
  const t = useT();
  if (actions.length === 0) return null;
  return (
    <Card data-testid="action-required">
      <CardContent className="pt-4 space-y-2">
        <CardTitle className="text-base">{t('training:action_required', 'Action required')}</CardTitle>
        <ul className="space-y-1.5">
          {actions.map((a, i) => {
            const Icon = ICON[a.kind] || CheckSquare;
            const overdue = a.kind === 'overdue';
            const row = (
              <div className="flex items-center gap-3">
                <Icon className={`w-4 h-4 shrink-0 ${overdue ? 'text-red-600 dark:text-red-400' : 'text-orange-500'}`} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-foreground truncate">{a.title}</div>
                  {a.due_at && <div className="text-xs text-muted-foreground">{t('training:due', 'Due')} {formatSessionRange(a.due_at, undefined, undefined, a.timezone)}{sessionZoneNote(a.timezone) ? ` · ${sessionZoneNote(a.timezone)}` : ''}</div>}
                </div>
                {a.required && <span className="text-[10px] font-semibold uppercase rounded-full bg-red-100 text-red-700 dark:bg-red-500/[0.16] dark:text-red-300 px-2 py-0.5">{t('training:required', 'Required')}</span>}
              </div>
            );
            const isPractice = a.kind === 'practice';
            const onClick = isPractice && onPractice ? () => onPractice(a) : a.session_id ? () => onOpen(a.session_id as string) : null;
            return (
              <li key={`${a.kind}-${a.assignment_id || a.session_id || i}`} className="rounded-md border border-border p-2.5" data-testid={`action-${a.kind}`}>
                {onClick
                  ? <button type="button" className="w-full text-left" onClick={onClick}>{row}</button>
                  : row}
                {isPractice && <div className="mt-1 pl-7 text-[11px] text-muted-foreground">{t('training:action_practice_hint', 'Practical exercise to submit')}</div>}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

export default ActionRequired;
